import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useEditor, EditorContent, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'

/**
 * Reusable Gmail-like rich-text editor built on TipTap.
 *
 * - `value` / `onChange` exchange **HTML** strings (editor.getHTML()).
 * - Toolbar: bold, italic, underline, strikethrough, bullet/ordered lists,
 *   blockquote, insert/edit/remove link, clear formatting.
 * - Keyboard shortcuts (Cmd/Ctrl+B/I/U) are provided by StarterKit + Underline.
 *
 * The component is uncontrolled internally (TipTap owns the document) but keeps
 * in sync with `value` when the parent resets it (e.g. clearing after send).
 */

export interface RichTextEditorHandle {
  /** Plain-text version of the current document (editor.getText()). */
  getText: () => string
  /** HTML version of the current document (editor.getHTML()). */
  getHTML: () => string
  /** Focus the editor. */
  focus: () => void
  /** Whether the document is empty (no meaningful content). */
  isEmpty: () => boolean
}

interface RichTextEditorProps {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  disabled?: boolean
  /** Fires on Cmd/Ctrl+Enter inside the editor. */
  onSubmit?: () => void
  /** Extra classes for the outer wrapper. */
  className?: string
  /** Min height of the editable area (px). Default 96. */
  minHeight?: number
  ariaLabel?: string
}

/** Treat the TipTap "empty document" HTML as empty. */
function htmlIsEmpty(html: string): boolean {
  const stripped = html
    .replace(/<p><\/p>/g, '')
    .replace(/<br\s*\/?>/g, '')
    .replace(/&nbsp;/g, '')
    .replace(/<[^>]*>/g, '')
    .trim()
  return stripped.length === 0
}

export const RichTextEditor = React.forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  (
    { value, onChange, placeholder, disabled = false, onSubmit, className = '', minHeight = 96, ariaLabel },
    ref
  ) => {
    const onSubmitRef = useRef(onSubmit)
    onSubmitRef.current = onSubmit

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: false,
          // keep history, bold, italic, strike, lists, blockquote, etc.
        }),
        Underline,
        Link.configure({
          openOnClick: false,
          autolink: true,
          HTMLAttributes: {
            target: '_blank',
            rel: 'noopener noreferrer nofollow',
          },
          validate: (href) => /^(https?:\/\/|mailto:)/i.test(href),
        }),
      ],
      content: value || '',
      editable: !disabled,
      editorProps: {
        attributes: {
          class:
            'prose prose-sm dark:prose-invert max-w-none focus:outline-none px-3.5 py-3 text-txt-primary',
          'aria-label': ariaLabel || placeholder || 'Rich text editor',
          role: 'textbox',
          'aria-multiline': 'true',
        },
        handleKeyDown: (_view, event) => {
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault()
            onSubmitRef.current?.()
            return true
          }
          return false
        },
      },
      onUpdate: ({ editor: ed }) => {
        const html = ed.getHTML()
        onChange(htmlIsEmpty(html) ? '' : html)
      },
    })

    // Keep the editor in sync when the parent resets `value` externally
    // (e.g. clearing the composer after a successful send, or loading a saved
    // signature). Guard against feedback loops by comparing to current HTML.
    useEffect(() => {
      if (!editor) return
      const current = editor.getHTML()
      const next = value || ''
      const bothEmpty = htmlIsEmpty(current) && htmlIsEmpty(next)
      if (!bothEmpty && current !== next) {
        editor.commands.setContent(next, false)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, editor])

    useEffect(() => {
      if (editor) editor.setEditable(!disabled)
    }, [disabled, editor])

    React.useImperativeHandle(
      ref,
      () => ({
        getText: () => editor?.getText() ?? '',
        getHTML: () => {
          const html = editor?.getHTML() ?? ''
          return htmlIsEmpty(html) ? '' : html
        },
        focus: () => editor?.chain().focus().run(),
        isEmpty: () => htmlIsEmpty(editor?.getHTML() ?? ''),
      }),
      [editor]
    )

    if (!editor) {
      return (
        <div
          className={`rounded-lg border border-border bg-surface-secondary ${className}`}
          style={{ minHeight: minHeight + 44 }}
        />
      )
    }

    return (
      <div
        className={`rounded-lg border border-border bg-surface-secondary focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500/20 transition-all overflow-hidden ${
          disabled ? 'opacity-60' : ''
        } ${className}`}
      >
        <Toolbar editor={editor} disabled={disabled} />
        <div
          className="overflow-y-auto rich-text-editor-content"
          data-empty={editor.isEmpty ? 'true' : undefined}
          style={{ ['--rte-placeholder' as string]: `"${(placeholder || '').replace(/"/g, '\\"')}"` }}
        >
          <EditorContent editor={editor} />
        </div>
        {/* min-height + empty-state placeholder (rendered via ::before so it
            never affects layout or steals clicks). */}
        <style>{`
          .rich-text-editor-content { max-height: 320px; }
          .rich-text-editor-content .ProseMirror { min-height: ${minHeight}px; }
          .rich-text-editor-content[data-empty="true"] .ProseMirror p:first-child::before {
            content: var(--rte-placeholder);
            color: var(--color-txt-tertiary);
            float: left;
            height: 0;
            pointer-events: none;
          }
        `}</style>
      </div>
    )
  }
)

RichTextEditor.displayName = 'RichTextEditor'

/* ─── Toolbar ─────────────────────────────────────────────────────────────── */

interface ToolbarProps {
  editor: Editor
  disabled: boolean
}

const Toolbar: React.FC<ToolbarProps> = ({ editor, disabled }) => {
  const [linkOpen, setLinkOpen] = useState(false)
  const [linkValue, setLinkValue] = useState('')
  const linkInputRef = useRef<HTMLInputElement>(null)

  // re-render the toolbar when selection / marks change so active states track
  const [, force] = useState(0)
  useEffect(() => {
    const update = () => force((n) => n + 1)
    editor.on('selectionUpdate', update)
    editor.on('transaction', update)
    return () => {
      editor.off('selectionUpdate', update)
      editor.off('transaction', update)
    }
  }, [editor])

  const openLinkEditor = useCallback(() => {
    const prev = editor.getAttributes('link').href as string | undefined
    setLinkValue(prev || 'https://')
    setLinkOpen(true)
    setTimeout(() => linkInputRef.current?.focus(), 0)
  }, [editor])

  const applyLink = useCallback(() => {
    const raw = linkValue.trim()
    if (!raw) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      setLinkOpen(false)
      return
    }
    // Normalise + validate: allow http(s) and mailto. Bare domains → https.
    let href = raw
    if (/^mailto:/i.test(href)) {
      // ok
    } else if (/^https?:\/\//i.test(href)) {
      // ok
    } else if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(href)) {
      href = `mailto:${href}`
    } else {
      href = `https://${href.replace(/^\/+/, '')}`
    }
    if (!/^(https?:\/\/|mailto:)/i.test(href)) return
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href, target: '_blank', rel: 'noopener noreferrer nofollow' })
      .run()
    setLinkOpen(false)
  }, [editor, linkValue])

  const removeLink = useCallback(() => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run()
    setLinkOpen(false)
  }, [editor])

  const btn = (
    active: boolean,
    onClick: () => void,
    title: string,
    children: React.ReactNode
  ) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      aria-pressed={active}
      className={`w-7 h-7 inline-flex items-center justify-center rounded-md text-txt-secondary hover:bg-surface-hover hover:text-txt-primary disabled:opacity-40 disabled:cursor-not-allowed transition-colors ${
        active ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300' : ''
      }`}
    >
      {children}
    </button>
  )

  const Divider = () => <span className="w-px h-5 bg-border mx-0.5" aria-hidden="true" />

  return (
    <div className="relative">
      <div className="flex items-center gap-0.5 flex-wrap px-2 py-1.5 border-b border-border bg-surface-secondary">
        {btn(editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), 'Bold (⌘B)', (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 4h7a4 4 0 010 8H6zM6 12h8a4 4 0 010 8H6z" />
          </svg>
        ))}
        {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), 'Italic (⌘I)', (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 4h-9M14 20H5M15 4L9 20" />
          </svg>
        ))}
        {btn(editor.isActive('underline'), () => editor.chain().focus().toggleUnderline().run(), 'Underline (⌘U)', (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 4v6a6 6 0 0012 0V4M5 20h14" />
          </svg>
        ))}
        {btn(editor.isActive('strike'), () => editor.chain().focus().toggleStrike().run(), 'Strikethrough', (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 12h16M16 6.5C16 5 14 4 12 4 9.5 4 8 5.3 8 7c0 1.3 1 2.2 2.5 2.8M8 17.5C8 19 10 20 12 20c2.5 0 4-1.3 4-3" />
          </svg>
        ))}

        <Divider />

        {btn(editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(), 'Bulleted list', (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01" />
          </svg>
        ))}
        {btn(editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), 'Numbered list', (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 6h11M10 12h11M10 18h11M3 5h1v4M3 9h2M4.5 15a1 1 0 11.9 1.5L3 19h2.5" />
          </svg>
        ))}
        {btn(editor.isActive('blockquote'), () => editor.chain().focus().toggleBlockquote().run(), 'Blockquote', (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 7H5a1 1 0 00-1 1v3a1 1 0 001 1h2a1 1 0 011 1v1a2 2 0 01-2 2M18 7h-2a1 1 0 00-1 1v3a1 1 0 001 1h2a1 1 0 011 1v1a2 2 0 01-2 2" />
          </svg>
        ))}

        <Divider />

        {btn(editor.isActive('link'), openLinkEditor, 'Insert / edit link', (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5a4 4 0 00-5.66 0l-2.5 2.5a4 4 0 005.66 5.66l1-1M10.5 13.5a4 4 0 005.66 0l2.5-2.5a4 4 0 00-5.66-5.66l-1 1" />
          </svg>
        ))}
        {editor.isActive('link') &&
          btn(false, removeLink, 'Remove link', (
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M18.36 5.64a4 4 0 010 5.66l-1.5 1.5M5.64 18.36a4 4 0 010-5.66l1.5-1.5M3 3l18 18" />
            </svg>
          ))}

        <Divider />

        {btn(
          false,
          () => editor.chain().focus().unsetAllMarks().clearNodes().run(),
          'Clear formatting',
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 5h12M9 5l-2 14M13 5l-1 7M5 19h6M14 14l6 6M20 14l-6 6" />
          </svg>
        )}
      </div>

      {/* Link popover */}
      {linkOpen && (
        <div
          className="absolute left-2 top-full mt-1 z-20 w-72 rounded-lg border border-border bg-surface shadow-float p-2.5 animate-slide-down"
          role="dialog"
          aria-label="Edit link"
        >
          <label className="block text-[11px] font-medium text-txt-secondary mb-1">Link URL</label>
          <input
            ref={linkInputRef}
            value={linkValue}
            onChange={(e) => setLinkValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                applyLink()
              } else if (e.key === 'Escape') {
                e.preventDefault()
                setLinkOpen(false)
              }
            }}
            placeholder="https://example.com or name@email.com"
            className="w-full px-2.5 py-1.5 rounded-md border border-border bg-surface-secondary text-sm text-txt-primary placeholder-txt-tertiary focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
          <div className="flex items-center justify-between gap-2 mt-2">
            <button
              type="button"
              onClick={() => setLinkOpen(false)}
              className="px-2.5 py-1 rounded-md text-xs font-medium text-txt-secondary hover:bg-surface-hover transition-colors"
            >
              Cancel
            </button>
            <div className="flex items-center gap-2">
              {editor.isActive('link') && (
                <button
                  type="button"
                  onClick={removeLink}
                  className="px-2.5 py-1 rounded-md text-xs font-medium text-error hover:bg-error-light/60 transition-colors"
                >
                  Remove
                </button>
              )}
              <button
                type="button"
                onClick={applyLink}
                className="px-2.5 py-1 rounded-md text-xs font-semibold bg-brand-600 text-white hover:bg-brand-700 transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
